// Multi-workspace-tabs rollout flag (MULTI_WORKSPACE_TABS.md Phase 5).
//
// ON by default since 2696043c (2026-07-21, "MWT default-ON (re-enabled) —
// boot-race guard fix now in place"). Corrected 2026-08-08 during the
// tickets/workspace.md P1 tenant-isolation audit — this header previously
// still described the EARLIER default-OFF revert (cccc347b, 2026-07-19),
// which is stale relative to the code below (`!== 'off'`, not `=== 'on'`).
// Do not re-derive the default from this comment alone; read `isMwtEnabled`.
//
// IMPORTANT for anyone auditing pointer/isolation bugs: because the OFF
// default briefly shipped (cccc347b, 2026-07-19 → 2696043c, 2026-07-21) and
// `localStorage.mwt` has no expiry, any browser that picked up
// `localStorage.mwt='off'` during that window (or via an explicit `?mwt=0`)
// stays pinned to the OLD shared-pointer behavior FOREVER, silently, even
// though the code has defaulted back to ON since 2026-07-21. Check
// `localStorage.getItem('mwt')` in the affected browser before assuming the
// pointer read is the shared one — it depends on that browser's history, not
// just what shipped to `main`.
//
// When ON (default) → per-tab workspace scoping: sessionStorage pointer,
// `/w/<org-slug>` URL prefix, and cross-tab/cross-device convergence gated
// per-tab. Opt OUT persistently with `localStorage.setItem('mwt', 'off')`, or
// transiently for one load/tab with `?mwt=0`. When off, the shell behaves
// EXACTLY as before MWT existed: shared `localStorage.activeWorkspace`
// pointer, no URL prefix, active convergence handlers.
//
// The `?mwt` query param wins over localStorage and is NOT persisted, so a single
// tab can force-enable (`?mwt=1`) or force-disable (`?mwt=0`) independently of the
// rest of the browser — which is how the two-tab isolation matrix is exercised.
//
// Pure module, direct ESM import (guestRoutes.js / workspacePrefix.js
// pattern) — AND barrelled via `functions/index.js` (`export *
// from './mwtFlag.js'`, line ~450); the "never registered" half of this
// comment went stale at some point after that landed.
//
// `_cached` sentinel is `null`, not `undefined` — frank's FA201 auto-fix
// wraps this module-scope mutable state in a `Smut` container that
// round-trips through JSON at `to-json` time, and `JSON.stringify` drops
// `undefined`-valued keys entirely, so the emitted Smut container never had
// a `_cached` key to write through (`frank to-json`: `unresolvable-
// identifier` on both `functions.isMwtEnabled` and its globalScope-lifted
// duplicate). `null` survives JSON round-tripping; same semantics
// everywhere `_cached` is read as "unset".
//
// FA204 / frank trap (a SEPARATE, more fundamental issue than the FA201 fix
// above addresses) — `_cached` is genuinely shared mutable state between TWO
// exports (isMwtEnabled + the test-only _resetMwtFlagCache), with no
// `el`/`root` argument anywhere in this file to hoist it onto
// (project_frank_drops_module_local_helpers). Re-homed onto
// `globalThis.__mwtFlagCache` (lazily created) — the same
// always-reachable-global escape hatch pointerCapability.js's
// `window.__pointerCapabilityState` uses. `globalThis` (not `window`) since
// this must also work in the SSR/Node test environment
// _resetMwtFlagCache is called from.
export const isMwtEnabled = () => {
  const cache = (globalThis.__mwtFlagCache ??= { value: null })
  if (cache.value !== null) return cache.value
  let on = false
  try {
    if (typeof window !== 'undefined' && window.location) {
      const q = new URLSearchParams(window.location.search || '').get('mwt')
      if (q === '1' || q === 'on') on = true
      else if (q === '0' || q === 'off') on = false
      // Default ON (re-enabled 2026-07-21). The 2026-07-19 default-ON launch was
      // reverted for an iPad reload loop — canonicalize-on-load's replaceState
      // fired a `locationchange` mid-boot → _authRouteGuard read a transient
      // hasSessionToken() false-negative on slow devices → `/`↔`/login` loop. That
      // boot-race is now fixed: _authRouteGuard trusts the seeded `root.signedIn`
      // (server-verified getMe) before hard-redirecting (app.js, cc6507f8). Opt OUT
      // per browser with localStorage.mwt='off', or per tab with ?mwt=0.
      else {
        // FILES-XWS-CALL-BLEED-1 — expiry for the stale opt-out described in
        // this file's header. `localStorage` has no TTL, so a browser that
        // picked up `mwt='off'` during the 2026-07-19 -> 2026-07-21 default-OFF
        // window stayed pinned to the shared-pointer behavior forever: with MWT
        // off `resolveActiveWorkspace` ignores the URL, so `/w/<org>/<ws>/…`
        // renders whatever workspace the pointer last held. That reads as
        // cross-workspace bleed to the person looking at the address bar.
        //
        // One-shot generation purge. A store with no `mwtOptOutGen` marker
        // predates this migration, so its `off` cannot be told apart from the
        // landmine — drop it once and stamp the store. Every opt-out made after
        // the migration carries the marker and is never touched, so the
        // documented `localStorage.setItem('mwt', 'off')` affordance keeps
        // working. Bump the marker value only for a new deliberate migration.
        const store = globalThis.localStorage
        if (store && store.getItem('mwtOptOutGen') !== '1') {
          if (store.getItem('mwt') === 'off') store.removeItem('mwt')
          store.setItem('mwtOptOutGen', '1')
        }
        on = store?.getItem('mwt') !== 'off'
      }
    }
  } catch {
    on = false
  }
  cache.value = on
  return on
}

// Test-only: forget the cached value so a suite can flip the flag between cases.
export const _resetMwtFlagCache = () => {
  const cache = (globalThis.__mwtFlagCache ??= { value: null })
  cache.value = null
}
